<template>
  <Head :title="`${t('page.title')} - ${course.name}`"></Head>
  <s-page-block size="md">
    <s-page-header :heading="t('page.title')" :description="t('page.description')" class="mb-4" />

    <form
      @submit.prevent="
        form.post(
          route('courses.assignments.import.store', {
            course: course.id,
          })
        )
      "
    >
      <s-card>
        <div class="stack vertical gap-4">
          <s-select-field
            v-if="availableInstitutions.length > 1"
            id="fromInstitution"
            :label="t('labels.fromInstitution')"
            :model-value="params.fromInstitutionId ?? course.institution.id"
            @update:modelValue="selectInstitution"
          >
            <option v-for="institution in availableInstitutions" :value="institution.id">
              {{ institution.name }}
            </option>
          </s-select-field>

          <div v-if="!availableCourses.length">
            <s-message color="red" icon="warning">
              {{ t('messages.noCoursesAvailable') }}
            </s-message>
          </div>

          <s-select-field
            id="fromInstitution"
            :label="t('labels.fromCourse')"
            :model-value="params.fromCourseId"
            @update:modelValue="selectCourse"
            :disabeld="!availableCourses.length"
          >
            <option v-for="course in availableCourses" :value="course.id">
              {{ course.code }} -
              {{ shortMonthYear(course.startDate.date) }}
            </option>
          </s-select-field>

          <div v-if="form.sourceCourseId" class="min-w-0 flex flex-col items-stretch gap-1 pb-1">
            <s-label>{{ t('labels.chooseAssignments', {course: course.code}) }}</s-label>
            <s-button-toggle
              id="chooseAssignments"
              name="chooseAssignments"
              :options="[
                {value: false, label: t('options.yes')},
                {value: true, label: t('options.no')},
              ]"
              v-model="form.chooseAssignments"
            />
          </div>
          <s-multi-select-field
            v-if="form.chooseAssignments"
            v-model="form.assignmentsToImport"
            id="assignedSections"
            :label="t('labels.assignmentsToImport')"
            placeholder="Please Select the Assignments to Import"
            :options="
              availableAssignments.map((option) => ({
                value: option.id,
                label: option.name,
              }))
            "
          >
            <template #selected="{selected}">
              <span class="comma-separated">
                <span v-for="option in selected">
                  {{ option.label }}
                </span>
              </span>
            </template>

            <template #option="{option}">
              <div class="flex justify-between w-full @container/task-select">
                <span class="flex-1 truncate">
                  {{ option.label }}
                </span>
              </div>
            </template>
          </s-multi-select-field>
        </div>
        <template #footer>
          <s-btn-group>
            <s-btn
              type="submit"
              icon="floppy"
              class="w-fit"
              :disabled="
                !params.fromCourseId || (form.chooseAssignments && !form.assignmentsToImport.length)
              "
              :processing="form.processing"
            >
              {{
                t(
                  'actions.importAssignments',
                  form.chooseAssignments
                    ? form.assignmentsToImport.length
                    : availableAssignments.length
                )
              }}
            </s-btn>
            <s-btn
              color="secondary"
              icon="close"
              :href="route('courses.assignments.index', course.id)"
            >
              {{ t('cancel') }}
            </s-btn>
          </s-btn-group>
        </template>
      </s-card>
    </form>
  </s-page-block>
</template>

<script setup lang="ts">
import {route} from 'ziggy-js';
import {router, useForm} from '@inertiajs/vue3';
import {Head} from '@inertiajs/vue3';
import {LaravelDate} from '../../types/laravel-date.js';
import {Course} from '../../types/entities/course.js';
import {Institution} from '../../types/entities/institution.js';
import {usePageParams} from '../../composables/usePageParams.js';
import SPageBlock from '../../design-system/SPageBlock.vue';
import SPageHeader from '../../design-system/SPageHeader.vue';
import SBtn from '../../design-system/SBtn.vue';
import SCard from '../../design-system/SCard.vue';
import SSelectField from '../../design-system/SSelectField.vue';
import SLabel from '../../design-system/SLabel.vue';
import SButtonToggle from '../../design-system/SButtonToggle.vue';
import SMultiSelectField from '../../design-system/controls/SMultiSelectField.vue';
import {shortMonthYear} from '../../format/dates.js';
import {useI18n} from 'vue-i18n';
import SMessage from '../../design-system/SMessage.vue';
import SBtnGroup from '../../design-system/SBtnGroup.vue';

type ImportSourceCourse = {
  id: number;
  code: string;
  startDate: LaravelDate;
};

type ImportSourceAssignment = {
  id: number;
  name: string;
};

const props = defineProps<{
  course: Course;
  availableInstitutions: Institution[];
  availableCourses: ImportSourceCourse[];
  availableAssignments: ImportSourceAssignment[];
}>();

const {t} = useI18n({
  inheritLocale: true,
  scope: 'local',
});

type PageParams = {
  fromInstitutionId?: number;
  fromCourseId?: number;
};

const params = usePageParams<PageParams>();

const form = useForm<{
  chooseAssignments: boolean;
  assignmentsToImport: number[];
  sourceCourseId?: number;
}>({
  chooseAssignments: false,
  assignmentsToImport: [],
  sourceCourseId: params.fromCourseId,
});

const selectInstitution = (institutionId: number) => {
  router.visit(
    route('courses.assignments.import.create', {
      course: props.course.id,
      _query: {
        fromInstitutionId: institutionId,
        fromCourseId: undefined,
      },
    })
  );
};

const selectCourse = (courseId: number) => {
  router.visit(
    route('courses.assignments.import.create', {
      course: props.course.id,
      _query: {
        fromInstitutionId: params.fromInstitutionId,
        fromCourseId: courseId,
      },
    })
  );
};
</script>

<i18n>
{
  "en": {
    "page.title": "Import Assignments",
    "page.description": "Speed up course creation by importing assignments from another course. You can only import assignments from courses in which you are a Course Admin",
    "actions.importAssignments": "Import Assignments | Import Assignment | Import {n} Assignments",
    "labels.fromInstitution": "From Institution",
    "labels.fromCourse": "From Course",
    "labels.chooseAssignments": "Import all assignments from {course}?",
    "labels.assignmentsToImport": "Assignments to Import",
    "options.yes": "Yes, copy all",
    "options.no": "No, let me choose",
    "messages.noCoursesAvailable": "No courses available. You are not a Course Admin in any other course."
  },
  "fr": {
    "page.title": "Importer des devoirs",
    "page.description": "Accélérez la création de cours en important des devoirs d'un autre cours. Vous ne pouvez importer des devoirs que depuis des cours dans lesquels vous êtes administrateur de cours.",
    "actions.importAssignments": "Importer le devoir | Importer {n} devoirs",
    "labels.fromInstitution": "De l'institution",
    "labels.fromCourse": "De la classe",
    "labels.chooseAssignments": "Importer tous les devoirs de {course} ?",
    "labels.assignmentsToImport": "Devoirs à importer",
    "options.yes": "Oui, copier tous",
    "options.no": "Non, laissez-moi choisir",
    "messages.noCoursesAvailable": "Aucun cours disponible. Vous n'êtes pas administrateur de cours dans un autre cours."
  }
}
</i18n>
<style scoped></style>
